import type { HookAPI } from "antd/es/modal/useModal"
import type { TFunction } from "react-i18next"

import { isHandledError } from "../../../errors/handled-error.ts"
import type { ProjectTypeDetail } from "../types/project.types.ts"

export const REPEATED_TYPE_CODE = "project_type_already_assigned"

/**
 * A project type as any payload names it. Only the project detail carries the
 * instance fields, and a backend deployed before them carries none of them.
 */
export interface ProjectTypeRef {
  id: string
  name: string
  assignment_id?: string | null
  display_name?: string | null
}

/** What addresses one instance; `id` names only the type, which may repeat. */
export const instanceKeyOf = (detail: ProjectTypeRef): string =>
  detail.assignment_id ?? detail.id

/** The name to show, numbered from the second instance of a type. */
export const typeLabel = (detail: ProjectTypeRef): string =>
  detail.display_name ?? detail.name

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const envelopeDetails = (value: unknown): Record<string, unknown> | null => {
  if (!isRecord(value)) return null
  const data = isRecord(value.data) ? value.data : value
  return isRecord(data.details) ? data.details : null
}

/**
 * Matched on the server's `code`, not its sentence, so rewording the sentence
 * cannot turn the confirm step back into a dead end. Reads the raw envelope
 * too: a rejected thunk throws `response.data`, not a HandledError.
 */
export const isRepeatedTypeRefusal = (error: unknown): boolean => {
  if (isHandledError(error)) return error.serverCode === REPEATED_TYPE_CODE
  return envelopeDetails(error)?.code === REPEATED_TYPE_CODE
}

export const confirmRepeatedType = (
  modal: HookAPI,
  t: TFunction
): Promise<boolean> =>
  new Promise((resolve) => {
    modal.confirm({
      title: t("projectTypeRepeat.title"),
      // Translated: the server's sentence arrives in English whatever the
      // user's language.
      content: t("projectTypeRepeat.body"),
      okText: t("projectTypeRepeat.confirm"),
      cancelText: t("projectTypeRepeat.cancel"),
      onOk: () => resolve(true),
      onCancel: () => resolve(false)
    })
  })

/** The instance just created: every instance of a type shares its `id`. */
export const newestInstanceOf = (
  details: ProjectTypeDetail[] | undefined,
  projectTypeId: string
): ProjectTypeDetail | undefined =>
  details
    ?.filter((detail) => detail.id === projectTypeId)
    .reduce<ProjectTypeDetail | undefined>(
      (newest, detail) =>
        !newest || (detail.occurrence ?? 1) > (newest.occurrence ?? 1)
          ? detail
          : newest,
      undefined
    )
